import {ConnectedFocusError} from 'focus-formik-error'
import {Field, Form, Formik} from 'formik'
import moment from 'moment'
import {RefObject, useRef, useState} from 'react'
import {useAuthUser} from 'react-auth-kit'
import {Modal, OverlayTrigger, Tooltip} from 'react-bootstrap'
import Flatpickr from 'react-flatpickr'
import {Crop} from 'react-image-crop'
import {useIntl} from 'react-intl'
import {NumericFormat} from 'react-number-format'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import Select from 'react-select'
import Toolbar, {
  formats,
  modules,
  modules_2,
  modules_3,
  modules_4,
  modules_5,
  QuillToolbar2,
  QuillToolbar3,
  QuillToolbar4,
  QuillToolbar5,
} from 'src/app/context/QuillConfig'
import {useUploadFile} from 'src/feature/bite/context/useMutation'
import CropAspect from 'src/feature/bite/hook/useCropAspect'
import {useGetDetailTermsAndConditions} from 'src/feature/store/context/useQuery'
import * as Yup from 'yup'
import {useCreateItem} from '../../context/useMutation'
import {
  useGetItemCategoryList,
  useGetItemSubCategoryList,
  useGetItemTypeList,
} from '../../context/useQuery'
import {useItem} from '../../hook'

export default function ItemForm(): JSX.Element {
  const intl = useIntl()
  const {isLoading: isSaving, mutateAsync} = useCreateItem()
  const {isLoading: isLoadingCategoryList} = useGetItemCategoryList()
  const {isLoading: isLoadingSubCategoryList} = useGetItemSubCategoryList()
  const {categories, subCategories, detail, detailSet} = useItem()
  const {data: itemTypes, isLoading: isFetchingItemTypes} = useGetItemTypeList()
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const auth = useAuthUser()
  const [image1, setImage1] = useState(null)
  const [tempImage, setTempImage] = useState('')
  const [crop, setCrop] = useState<Crop>()
  const [resizeImageModal, setResizeImageModal] = useState(false)

  const descriptionLimit = 2000
  const {isLoading: isLoadingUpload, mutateAsync: mutateAsyncUpload} = useUploadFile()

  const reactQuillRef: RefObject<ReactQuill> = useRef<ReactQuill>(null)
  const [message, setMessage] = useState<string>('')
  const [isRichTextCharacterLimitExceeded, setIsRichTextCharacterLimitExceeded] =
    useState<boolean>(false)
  const {data: isTermsAndConditionsAvailable, isLoading: isLoadingTermsAndConditionAvailable} =
    useGetDetailTermsAndConditions()

  const uploadFile = async (file: File) => {
    const data = await mutateAsyncUpload(file)
    return data
  }

  const checkCharacterCount = (event: any) => {
    const unprivilegedEditor = (reactQuillRef?.current as any)?.unprivilegedEditor
    if (unprivilegedEditor.getLength() > descriptionLimit + 1 && event.key !== 'Backspace')
      event.preventDefault()
  }

  const calculateCharacterCount = (value: string) => {
    const removedTags = value?.replace(/<[^>]*>/g, '')
    if (removedTags.length > descriptionLimit) setIsRichTextCharacterLimitExceeded(true)
    else setIsRichTextCharacterLimitExceeded(false)
  }

  const onSubmit = async (values, {setSubmitting, resetForm}) => {
    try {
      setIsEditing(false)
      values.taxRate = 5

      if (values.directSale === false) {
        values.price = 0
        values.purchaseType = 0
        values.eStore = false
        values.paymentLink = false
        values.imagesData = null
      }

      if (values.isSubscriptionPackage === false) {
        values.duration = 1
        values.usageLimit = 1
      }

      // start: bu alan BE tarafında çok karışık bir şekilde
      // ileride hataya sebep olmaması için refactor edilmeli
      // normal şartlarda bütün additional informations alanları
      // infoContentData içinde bulunuyor
      // ancak Terms and Conditions alanı BE tarafından dışarıda tanımlanmış
      // bu yüzden mutate öncesi manipülasyon yapıyorum.
      values.termsAndConditions = values.infoContentData.find(
        (item) => item.name === 'Terms and Conditions'
      )?.data

      values.infoContentData = values.infoContentData.filter(
        (item) => item.name !== 'Terms and Conditions'
      )
      // end: manipülasyon

      await mutateAsync({
        ...values,
      })

      setSubmitting(false)
      setIsEditing(false)
    } catch (error) {
      setSubmitting(false)
      setIsEditing(false)
    }
  }

  const status = [
    {value: 1, label: intl.formatMessage({id: 'ACTIVE'})},
    {value: 2, label: intl.formatMessage({id: 'INACTIVE'})},
  ]

  const tooltips = [
    {
      title: 'Feature and Benefits',
      content:
        'Provide a detailed overview of the product’s features and benefits, including what customers will gain from purchasing.',
    },
    {
      title: 'Eligibility',
      content:
        'Define any specific requirements or conditions that customers must meet to be eligible for this product.',
    },
    {
      title: 'Additional Benefits',
      content: 'List any extra perks or advantages that come with this product.',
    },
    // Terms and Conditions sadece isTermsAndConditionsAvailable true ise eklenecek
    ...(isTermsAndConditionsAvailable
      ? [
          {
            title: 'Terms and Conditions',
            content:
              'Include any specific terms and conditions that apply to the purchase of this product, such as usage rights or restrictions.',
          },
        ]
      : []),
    {
      title: 'Cancellation and Refund Policy',
      content:
        'Clearly outline the terms and conditions related to canceling the purchase, including any applicable fees or restrictions.',
    },
  ]

  const mockUpPurchaseTypeSelect = [
    {value: 1, label: 'Vasco App'},
    // { value: 1, label: 'Online' },
    // { value: 2, label: 'In Store' },
    // { value: 3, label: 'Both' },
  ]

  const validSchema = Yup.object({
    name: Yup.string().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    isSubscriptionPackage: Yup.boolean().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    detail: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .test(
        'description',
        `Description must be less than ${descriptionLimit} characters`,
        function (value) {
          let htmlEl = document.createElement('div')
          htmlEl.innerHTML = value
          let text = htmlEl.innerText

          if (text.length > descriptionLimit) {
            return false
          }

          return true
        }
      )
      .test('description', 'Description is required', function (value) {
        let htmlEl = document.createElement('div')
        htmlEl.innerHTML = value
        let text = htmlEl.innerText

        if (text.length === 0) {
          return false
        }

        return true
      }),
    categoryID: Yup.string().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    subCategoryID: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    status: Yup.number().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    duration: Yup.number().when(['isSubscriptionPackage'], ([isSubscriptionPackage], schema) => {
      return isSubscriptionPackage
        ? schema
            .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
            .min(1, intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_VALUE'}, {min: 1}))
        : schema.nullable()
    }),
    availableFrom: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    availableUntil: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .test('availableUntil', 'End Date must be greater than Publish Date', function (value) {
        const {availableFrom} = this.parent
        const availableFromMoment = moment(availableFrom, 'DD-MM-YYYY HH:mm')
        const availableUntilMoment = moment(value, 'DD-MM-YYYY HH:mm')

        if (availableUntilMoment.isSameOrBefore(availableFromMoment)) {
          return this.createError({
            path: this.path,
            message: intl.formatMessage({
              id: 'GLOBAL.VALIDATION.END_DATE_MUST_BE_GREATER_THAN_START_DATE',
            }),
          })
        } else {
          return true
        }
      }),
    sku: Yup.string(),
    usageLimit: Yup.number().when(['isSubscriptionPackage'], ([isSubscriptionPackage], schema) => {
      return isSubscriptionPackage
        ? schema
            .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
            .min(1, intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_VALUE'}, {min: 1}))
        : schema.nullable()
    }),
    barcode: Yup.string(),
    itemTypeID: Yup.string().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    price: Yup.number().when(['directSale'], ([directSale], schema) => {
      return directSale
        ? schema
            .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
            .min(1, intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_VALUE'}, {min: 1}))
        : schema
            .nullable()
            .min(-9999, intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_VALUE'}, {min: -9999}))
    }),
    taxRate: Yup.number(),
    purchaseType: Yup.number().when(['directSale'], ([directSale], schema) => {
      return directSale
        ? schema.required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
        : schema.nullable()
    }),
    directSale: Yup.boolean().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    eStore: Yup.boolean().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    paymentLink: Yup.boolean().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    infoContentData: Yup.array(
      Yup.object({
        id: Yup.string().optional().nullable(),
        name: Yup.string().optional().nullable(),
        contentURL: Yup.string().optional().nullable(),
        data: Yup.string()
          .optional()
          .nullable()
          .test('data', 'Data must be less than 1000 characters', function (value) {
            const removedTags = value?.replace(/<[^>]*>/g, '')
            if (removedTags && removedTags?.length > 1000) return false
            return true
          }),
      })
        .optional()
        .nullable()
    ),
    imagesData: Yup.array().test(
      'image',
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}),
      (value, context) => {
        if ((value?.length === 0 || !value) && context.parent.directSale) {
          return false
        }

        return true
      }
    ),
  })

  if (isLoadingTermsAndConditionAvailable) {
    return <div>Loading...</div>
  }

  return (
    <Formik
      initialValues={{
        name: '',
        detail: '',
        categoryID: '',
        subCategoryID: '',
        status: 1,
        duration: null,
        availableFrom: '',
        availableUntil: '',
        sku: '',
        isSubscriptionPackage: false,
        usageLimit: 1,
        barcode: '',
        itemTypeID: '1',
        price: 0.0,
        taxRate: '5',
        purchaseType: 1,
        directSale: true,
        eStore: true,
        paymentLink: true,
        infoContentData: [
          {
            name: 'Feature and Benefits',
            data: '',
            tooltip:
              "Provide a detailed overview of the subscription's features and benefits, including what customers will gain from subscribing.",
          },
          {
            name: 'Eligibility',
            data: '',
            tooltip:
              'Define any specific requirements or conditions that customers must meet to be eligible for this membership.',
          },
          {
            name: 'Additional Benefits',
            data: '',
            tooltip: 'List any extra perks or advantages that come with this membership. ',
          },
          ...(isTermsAndConditionsAvailable
            ? [
                {
                  name: 'Terms and Conditions',
                  data: '',
                  tooltip:
                    'Include any specific terms and conditions that apply to the membership, such as usage rights or restrictions.',
                },
              ]
            : []),
          {
            name: 'Cancellation and Refund Policy',
            data: '',
            tooltip:
              'Clearly outline the terms and conditions related to canceling the membership, including any applicable fees or restrictions.',
          },
        ],
        imagesData: [] as any,
      }}
      validationSchema={validSchema}
      onSubmit={onSubmit}
    >
      {(prop: any) => {
        return (
          <>
            <Modal
              show={resizeImageModal}
              onHide={() => setResizeImageModal(false)}
              size='lg'
              centered
              backdrop='static'
            >
              <Modal.Header closeButton>
                <Modal.Title>Crop Image</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {tempImage && (
                  <div className='d-flex flex-column align-items-center'>
                    <CropAspect
                      values={prop.values}
                      crop={crop}
                      setCrop={setCrop}
                      imgSrc={tempImage}
                      setImgSrc={setTempImage}
                      selectedImageIndex={selectedImageIndex}
                      setResizeImageModal={setResizeImageModal}
                      setImage1={setImage1}
                      setFieldValue={prop.setFieldValue}
                    />
                  </div>
                )}
              </Modal.Body>
            </Modal>
            <Form className='form d-flex flex-column flex-lg-row fv-plugins-bootstrap5 fv-plugins-framework'>
              <ConnectedFocusError />
              <div className='d-flex flex-column flex-row-fluid gap-7 gap-lg-12'>
                <div className='tab-content'>
                  <div
                    className='tab-pane fade active show'
                    id='kt_ecommerce_add_product_general'
                    role='tabpanel'
                  >
                    <div className='d-flex flex-column gap-7 gap-lg-10'>
                      <div className='card card-flush py-4'>
                        <div className='card-header'>
                          <div className='card-title m-0'>
                            <div>
                              <h3 className='fw-bold mb-2 mt-5'>
                                {intl.formatMessage({id: 'ITEM.INFO'})}
                              </h3>
                              <p className='text-muted fw-normal fs-6'>
                                Create a new product to be offered individually or as part of a
                                membership. Products can be configured with flexible usage rules,
                                visibility options, and pricing preferences depending on your
                                business needs.
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className='card-body pt-0'>
                          <div className='fv-row fv-plugins-icon-container'>
                            <div className='row mb-3'>
                              <div className='col-12'>
                                <label className='required form-label'>
                                  {intl.formatMessage({id: 'ITEM.NAME'})}
                                  <OverlayTrigger
                                    placement='top'
                                    overlay={
                                      <Tooltip>
                                        Enter a clear and descriptive name for the product.
                                      </Tooltip>
                                    }
                                  >
                                    <i className='bi bi-info-circle text-primary ms-1'></i>
                                  </OverlayTrigger>
                                </label>

                                <Field
                                  type='text'
                                  name='name'
                                  id='name'
                                  className='form-control mb-2'
                                  placeholder={intl.formatMessage({
                                    id: 'ITEM.NAME',
                                  })}
                                  maxLength={51}
                                />

                                <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                  {prop.errors.name && prop.touched.name && (
                                    <div className='fv-help-block'>{prop.errors.name}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className='row mb-3'>
                              <div className='col-12'>
                                <label className='required form-label'>
                                  {intl.formatMessage({
                                    id: 'ITEM.DESCRIPTION',
                                  })}
                                  <OverlayTrigger
                                    placement='top'
                                    overlay={
                                      <Tooltip>
                                        Provide a detailed description, including key features,
                                        benefits, or details about the product.
                                      </Tooltip>
                                    }
                                  >
                                    <i className='bi bi-info-circle text-primary ms-1'></i>
                                  </OverlayTrigger>
                                </label>

                                <Toolbar />
                                <ReactQuill
                                  placeholder={intl.formatMessage({id: 'DESCRIPTION'})}
                                  value={prop.values.detail}
                                  formats={formats}
                                  modules={modules}
                                  onChange={(value) => {
                                    prop.setFieldValue('detail', value)
                                    /*   calculateCharacterCount(value)
                                    setMessage(value) */
                                  }}
                                  ref={reactQuillRef}
                                  onKeyDown={checkCharacterCount}
                                />

                                {/*            {isRichTextCharacterLimitExceeded && (
                                <div className='fv-plugins-message-container mt-2'>
                                  <div className='fv-help-block'>
                                    {intl.formatMessage(
                                      {
                                        id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD',
                                      },
                                      {length: descriptionLimit}
                                    )}
                                  </div>
                                </div>
                              )}
 */}
                                <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                  {prop.errors.detail && prop.touched.detail && (
                                    <div className='fv-help-block'>{prop.errors.detail}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className='row mb-3'>
                              <div className='col-2'>
                                <label className='required form-label'>
                                  {intl.formatMessage({id: 'ITEM.STATUS'})}
                                  <OverlayTrigger
                                    placement='top'
                                    overlay={
                                      <Tooltip>
                                        Set the current availability status of the product. Only
                                        active products will be available for sale or use.
                                      </Tooltip>
                                    }
                                  >
                                    <i className='bi bi-info-circle text-primary ms-1'></i>
                                  </OverlayTrigger>
                                </label>
                                <Select
                                  className='custom-select'
                                  options={status}
                                  value={status.find((s) => s.value === prop.values.status)}
                                  onChange={(value) => {
                                    prop.setFieldValue('status', value?.value)
                                  }}
                                />
                                <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                  {prop.errors.status && prop.touched.status && (
                                    <div className='fv-help-block'>{prop.errors.status}</div>
                                  )}
                                </div>
                              </div>

                              <div className='col-3'>
                                <label className='required form-label'>
                                  {intl.formatMessage({id: 'ITEM.PUBLISH_DATE'})}
                                  <OverlayTrigger
                                    placement='top'
                                    overlay={
                                      <Tooltip>
                                        Specify the date when the product will become available.
                                      </Tooltip>
                                    }
                                  >
                                    <i className='bi bi-info-circle text-primary ms-1'></i>
                                  </OverlayTrigger>
                                </label>
                                <Flatpickr
                                  id='availableFrom'
                                  className='form-control mb-2'
                                  name='availableFrom'
                                  placeholder={intl.formatMessage({
                                    id: 'ITEM.PUBLISH_DATE',
                                  })}
                                  value={prop.values.availableFrom}
                                  options={{
                                    dateFormat: 'd-m-Y H:i',
                                    enableTime: true,
                                    time_24hr: true,
                                    minDate: moment().format('DD-MM-YYYY HH:mm'),
                                  }}
                                  onChange={(date) => {
                                    const _date = moment(date[0]).format('DD-MM-YYYY HH:mm')
                                    prop.setFieldValue('availableFrom', _date)
                                  }}
                                />
                                <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                  {prop.errors.availableFrom && prop.touched.availableFrom && (
                                    <div className='fv-help-block'>{prop.errors.availableFrom}</div>
                                  )}
                                </div>
                              </div>

                              <div className='col-3'>
                                <label className='required form-label'>
                                  {intl.formatMessage({id: 'ITEM.END_DATE'})}
                                  <OverlayTrigger
                                    placement='top'
                                    overlay={
                                      <Tooltip>
                                        Set the date when the product will no longer be available.
                                      </Tooltip>
                                    }
                                  >
                                    <i className='bi bi-info-circle text-primary ms-1'></i>
                                  </OverlayTrigger>
                                </label>
                                <Flatpickr
                                  id='availableUntil'
                                  name='availableUntil'
                                  className='form-control mb-2'
                                  placeholder={intl.formatMessage({
                                    id: 'ITEM.END_DATE',
                                  })}
                                  value={prop.values.availableUntil}
                                  options={{
                                    dateFormat: 'd-m-Y H:i',
                                    enableTime: true,
                                    time_24hr: true,
                                  }}
                                  onChange={(date) => {
                                    const _date = moment(date[0]).format('DD-MM-YYYY HH:mm')
                                    prop.setFieldValue('availableUntil', _date)
                                  }}
                                />
                                <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                  {prop.errors.availableUntil && prop.touched.availableUntil && (
                                    <div className='fv-help-block'>
                                      {prop.errors.availableUntil}
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className='col-2'>
                                <div
                                  className={`${
                                    !prop.values.isSubscriptionPackage ? 'd-none' : ''
                                  }`}
                                >
                                  <label className='required form-label'>
                                    Duration
                                    <OverlayTrigger
                                      placement='top'
                                      overlay={
                                        <Tooltip>
                                          Define the duration (in months) your customers will have
                                          access to this product after purchase.
                                        </Tooltip>
                                      }
                                    >
                                      <i className='bi bi-info-circle text-primary ms-1'></i>
                                    </OverlayTrigger>
                                  </label>

                                  <NumericFormat
                                    name='duration'
                                    className='form-control mb-2'
                                    placeholder='Months'
                                    decimalScale={0}
                                    min={1}
                                    maxLength={3}
                                    allowNegative={false}
                                    allowLeadingZeros={false}
                                    value={prop.values.duration}
                                    onValueChange={(values) => {
                                      prop.setFieldValue('duration', values.floatValue)
                                    }}
                                    onBlur={(e) => {
                                      prop.setFieldTouched('duration', true)
                                    }}
                                    max={999}
                                  />

                                  <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                    {prop.errors.duration && prop.touched.duration && (
                                      <div className='fv-help-block'>{prop.errors.duration}</div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className='row mb-3'>
                              <div className='col-2'>
                                <label className='required form-label'>
                                  {intl.formatMessage({
                                    id: 'ITEM.CATEGORY',
                                  })}

                                  <OverlayTrigger
                                    placement='top'
                                    overlay={
                                      <Tooltip>
                                        Select the main category that best describes the product.
                                      </Tooltip>
                                    }
                                  >
                                    <i className='bi bi-info-circle text-primary ms-1'></i>
                                  </OverlayTrigger>
                                </label>

                                <Select
                                  className='custom-select'
                                  placeholder={intl.formatMessage({
                                    id: 'ITEM.CATEGORY',
                                  })}
                                  options={categories?.map((item) => ({
                                    value: item.id,
                                    label: item.name,
                                  }))}
                                  value={categories
                                    ?.filter((s) => s.id === prop.values.categoryID)
                                    ?.map((item) => ({
                                      value: item.id,
                                      label: item.name,
                                    }))}
                                  onChange={(value) => {
                                    prop.setFieldValue('categoryID', value?.value)
                                    prop.setFieldValue('subCategoryID', '')

                                    detailSet({
                                      ...detail,
                                      subCategoryID: '',
                                    })

                                    detailSet({
                                      ...detail,
                                      categoryID: value?.value || '',
                                    })
                                  }}
                                  isLoading={isLoadingCategoryList}
                                />

                                <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                  {prop.errors.categoryID && prop.touched.categoryID && (
                                    <div className='fv-help-block'>{prop.errors.categoryID}</div>
                                  )}
                                </div>
                              </div>

                              <div className='col-2'>
                                <label className='required form-label'>
                                  {intl.formatMessage({
                                    id: 'PRODUCT.LIST.SUB_CATEGORY',
                                  })}

                                  <OverlayTrigger
                                    placement='top'
                                    overlay={
                                      <Tooltip>
                                        Choose a subcategory to further define the product.
                                      </Tooltip>
                                    }
                                  >
                                    <i className='bi bi-info-circle text-primary ms-1'></i>
                                  </OverlayTrigger>
                                </label>

                                <Select
                                  className='custom-select'
                                  placeholder={intl.formatMessage({
                                    id: 'PRODUCT.LIST.SUB_CATEGORY',
                                  })}
                                  options={subCategories?.map((item) => ({
                                    value: item.id,
                                    label: item.name,
                                  }))}
                                  value={subCategories
                                    ?.filter((s) => s.id === prop.values.subCategoryID)
                                    ?.map((item) => ({
                                      value: item.id,
                                      label: item.name,
                                    }))}
                                  onChange={(value) => {
                                    prop.setFieldValue('subCategoryID', value?.value)
                                  }}
                                  isLoading={isLoadingCategoryList}
                                />

                                <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                  {prop.errors.subCategoryID && prop.touched.subCategoryID && (
                                    <div className='fv-help-block'>{prop.errors.subCategoryID}</div>
                                  )}
                                </div>
                              </div>

                              <div className='col-2'>
                                <label className='required form-label'>
                                  {intl.formatMessage({id: 'ITEM.TYPE'})}

                                  <OverlayTrigger
                                    placement='top'
                                    overlay={
                                      <Tooltip>
                                        Specify the type of service this product represents (e.g.,
                                        session, event).
                                      </Tooltip>
                                    }
                                  >
                                    <i className='bi bi-info-circle text-primary ms-1'></i>
                                  </OverlayTrigger>
                                </label>
                                <Select
                                  id='itemTypeID'
                                  name='itemTypeID'
                                  className='custom-select'
                                  options={
                                    itemTypes?.map((item) => ({
                                      value: item.id,
                                      label: item.name,
                                    })) || []
                                  }
                                  value={itemTypes?.find((s) => s.value === prop.values.itemTypeID)}
                                  onChange={(value) => {
                                    prop.setFieldValue('itemTypeID', value?.value)
                                  }}
                                />
                                <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                  {prop.errors.itemTypeID && prop.touched.itemTypeID && (
                                    <div className='fv-help-block'>{prop.errors.itemTypeID}</div>
                                  )}
                                </div>
                              </div>

                              <div className='col-2'>
                                <label className='form-label'>
                                  {intl.formatMessage({id: 'PRODUCT.INPUT.SKU_ID'})}
                                  <OverlayTrigger
                                    placement='top'
                                    overlay={
                                      <Tooltip>
                                        Enter a unique identifier for the item, useful for tracking
                                        purposes.
                                      </Tooltip>
                                    }
                                  >
                                    <i className='bi bi-info-circle text-primary ms-1'></i>
                                  </OverlayTrigger>
                                </label>
                                <Field
                                  type='text'
                                  name='sku'
                                  id='sku'
                                  className='form-control mb-2'
                                  placeholder={intl.formatMessage({
                                    id: 'PRODUCT.INPUT.SKU_ID',
                                  })}
                                  maxLength={51}
                                  value={prop.values?.sku}
                                  onChange={(e) => {
                                    prop.setFieldValue('sku', e.target.value)
                                  }}
                                />
                                <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                  {prop.errors.sku && prop.touched.sku && (
                                    <div className='fv-help-block'>{prop.errors.sku}</div>
                                  )}
                                </div>
                              </div>

                              <div className='col-2'>
                                <div
                                  className={`${
                                    !prop.values.isSubscriptionPackage ? 'd-none' : ''
                                  }`}
                                >
                                  <label className='form-label required'>
                                    Usage Limit
                                    <OverlayTrigger
                                      placement='top'
                                      overlay={
                                        <Tooltip>
                                          Define how many times this product can be used.
                                        </Tooltip>
                                      }
                                    >
                                      <i className='bi bi-info-circle text-primary ms-1'></i>
                                    </OverlayTrigger>
                                  </label>
                                  <NumericFormat
                                    type='text'
                                    name='usageLimit'
                                    id='usageLimit'
                                    className='form-control mb-2'
                                    placeholder='Usage Limit'
                                    maxLength={4}
                                    disabled={!prop.values.isSubscriptionPackage}
                                    readOnly={!prop.values.isSubscriptionPackage}
                                    decimalScale={0}
                                    allowNegative={false}
                                    value={prop.values?.usageLimit}
                                    onChange={(e) => {
                                      prop.setFieldValue('usageLimit', e.target.value)
                                    }}
                                  />
                                  <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                    {prop.errors.usageLimit && prop.touched.usageLimit && (
                                      <div className='fv-help-block'>{prop.errors.usageLimit}</div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className='d-flex flex-column gap-5 mt-5'>
                              <div>
                                <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid d-flex align-items-center gap-3'>
                                  <Field
                                    className='form-check-input'
                                    type='checkbox'
                                    id='directSale'
                                    name='directSale'
                                    defaultChecked={true}
                                    checked={prop.values.directSale}
                                    onChange={(e) => {
                                      prop.setFieldValue('directSale', e.target.checked)
                                      if (!e.target.checked) {
                                        prop.setFieldValue('isSubscriptionPackage', false)
                                        prop.setFieldValue('usageLimit', 1)
                                      }
                                    }}
                                  />
                                  <label className='required form-label p-0 m-0'>
                                    {intl.formatMessage({id: 'ITEM.DIRECT.SALE'})}
                                    <OverlayTrigger
                                      placement='top'
                                      overlay={
                                        <Tooltip>
                                          Enable this to allow the product to be sold individually
                                          through the Vasco Life App or via payment link.
                                        </Tooltip>
                                      }
                                    >
                                      <i className='bi bi-info-circle text-primary ms-1'></i>
                                    </OverlayTrigger>
                                  </label>
                                </div>
                              </div>
                              <div>
                                <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid d-flex align-items-center gap-3'>
                                  <Field
                                    className='form-check-input'
                                    type='checkbox'
                                    id='isSubscriptionPackage'
                                    name='isSubscriptionPackage'
                                    defaultChecked={true}
                                    disabled={!prop.values.directSale}
                                    checked={prop.values.isSubscriptionPackage}
                                    onChange={(e) => {
                                      prop.setFieldValue('isSubscriptionPackage', e.target.checked)

                                      if (!e.target.checked) {
                                        prop.setFieldValue('usageLimit', 1)
                                      }
                                    }}
                                  />
                                  <label className='required form-label p-0 m-0'>
                                    Product as Membership
                                    <OverlayTrigger
                                      placement='top'
                                      overlay={
                                        <Tooltip>
                                          Enable this option to define a duration and usage limit
                                          for the product, allowing it to function like a
                                          membership.
                                        </Tooltip>
                                      }
                                    >
                                      <i className='bi bi-info-circle text-primary ms-1'></i>
                                    </OverlayTrigger>
                                  </label>
                                </div>
                              </div>
                              <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid d-flex align-items-center gap-3'>
                                <Field
                                  id='paymentLink'
                                  name='paymentLink'
                                  className='form-check-input'
                                  type='checkbox'
                                  value=''
                                  defaultChecked={true}
                                  disabled={!prop.values.directSale}
                                  checked={
                                    !prop.values.directSale ? false : prop.values.paymentLink
                                  }
                                  onChange={(e) => {
                                    prop.setFieldValue('paymentLink', e.target.checked)
                                  }}
                                />
                                <label className='required form-label p-0 m-0'>
                                  {intl.formatMessage({id: 'LABEL.PAYMENT.LINK'})}

                                  <OverlayTrigger
                                    placement='top'
                                    overlay={
                                      <Tooltip>
                                        Enable to generate a reusable or customer-specific payment
                                        link for this product.
                                      </Tooltip>
                                    }
                                  >
                                    <i className='bi bi-info-circle text-primary ms-1'></i>
                                  </OverlayTrigger>
                                </label>
                              </div>
                              <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid d-flex align-items-center gap-3'>
                                <Field
                                  id='eStore'
                                  name='eStore'
                                  className='form-check-input'
                                  type='checkbox'
                                  disabled={!prop.values.directSale}
                                  checked={!prop.values.directSale ? false : prop.values.eStore}
                                  onChange={(e) => {
                                    prop.setFieldValue('eStore', e.target.checked)
                                  }}
                                  defaultChecked={true}
                                />
                                <label className='required form-label p-0 m-0'>
                                  {intl.formatMessage({id: 'LABEL.E.STORE'})}

                                  <OverlayTrigger
                                    placement='top'
                                    overlay={
                                      <Tooltip>
                                        Enable to make the product visible and purchasable on the
                                        Vasco Life App’s store page.
                                      </Tooltip>
                                    }
                                  >
                                    <i className='bi bi-info-circle text-primary ms-1'></i>
                                  </OverlayTrigger>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {prop.values.directSale && (
                        <div className='card card-flush py-4'>
                          <div className='card-header pb-5'>
                            <div className='card-title m-0'>
                              <div>
                                <h3 className='fw-bold mb-2 mt-5'>
                                  {intl.formatMessage({id: 'LABEL.PRICING.DETAILS'})}
                                </h3>
                                <p className='text-muted fw-normal fs-6'>
                                  Set the pricing information for the product.
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className='card-body pt-0 w-100'>
                            <div className='row'>
                              <div className='col-4'>
                                <label className='form-label'>
                                  {intl.formatMessage({id: 'PRODUCT.INPUT.PURCHASE_TYPE'})}

                                  <OverlayTrigger
                                    placement='top'
                                    overlay={
                                      <Tooltip>
                                        Select how the service or product can be purchased.
                                      </Tooltip>
                                    }
                                  >
                                    <i className='bi bi-info-circle text-primary ms-1'></i>
                                  </OverlayTrigger>
                                </label>

                                <Select
                                  className='custom-select'
                                  options={mockUpPurchaseTypeSelect}
                                  value={mockUpPurchaseTypeSelect.find(
                                    (s) => s.value === prop.values.purchaseType
                                  )}
                                  onChange={(value) => {
                                    prop.setFieldValue('purchaseType', value?.value)
                                  }}
                                />

                                <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                  {prop.errors.purchaseType && prop.touched.purchaseType && (
                                    <div className='fv-help-block'>{prop.errors.purchaseType}</div>
                                  )}
                                </div>
                              </div>

                              <div className='col-4'>
                                <div className='mb-10 fv-row fv-plugins-icon-container'>
                                  <label className='required form-label'>
                                    {intl.formatMessage({id: 'LABEL.TOTAL.PRICE'})}

                                    <OverlayTrigger
                                      placement='top'
                                      overlay={
                                        <Tooltip>
                                          Set the total price customers will pay for this product,
                                          including any applicable tax.
                                        </Tooltip>
                                      }
                                    >
                                      <i className='bi bi-info-circle text-primary ms-1'></i>
                                    </OverlayTrigger>
                                  </label>

                                  <NumericFormat
                                    type='text'
                                    name='price'
                                    className='form-control mb-2'
                                    placeholder={intl.formatMessage({id: 'LABEL.TOTAL.PRICE'})}
                                    maxLength={14}
                                    decimalSeparator='.'
                                    thousandSeparator=','
                                    fixedDecimalScale
                                    prefix='AED '
                                    value={prop.values?.price || 0.0}
                                    onValueChange={(values) => {
                                      prop.setFieldValue('price', values.floatValue || 0.0)
                                    }}
                                    decimalScale={2}
                                    allowNegative={false}
                                    allowLeadingZeros={false}
                                  />

                                  <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                    {prop.errors.price && prop.touched.price && (
                                      <div className='fv-help-block'>{prop.errors.price}</div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {prop.values.directSale && (
                        <div className='card card-flush py-4'>
                          <div className='card-header pb-5'>
                            <div className='card-title m-0'>
                              <div>
                                <h3 className='fw-bold mb-2 mt-5'>Image Section</h3>
                                <p className='text-muted fw-normal fs-6'>
                                  Upload a representative visual for the product to support accurate
                                  display across listings and detail views.
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className='card-body pt-0 w-100'>
                            <div className='d-flex gap-3'>
                              {[0, 1, 2].map((index) => (
                                <div
                                  key={index}
                                  className='image-input image-input-outline border border-dashed bg-light d-flex align-items-center'
                                  style={{
                                    maxWidth: '150px',
                                    maxHeight: '150px',
                                    minWidth: '150px',
                                    minHeight: '150px',
                                    position: 'relative',
                                  }}
                                >
                                  {prop.values.imagesData?.[index]?.imageFullUrl ||
                                  prop.values.imagesData?.[index]?.imageUrl ? (
                                    <img
                                      src={
                                        prop.values?.imagesData?.[index]?.imageFullUrl?.includes(
                                          'http'
                                        )
                                          ? prop.values.imagesData?.[index]?.imageFullUrl
                                          : `${process.env.REACT_APP_IMG_DOMAIN}${prop.values.imagesData?.[index]?.imageUrl}`
                                      }
                                      alt=''
                                      className='rounded'
                                      style={{
                                        objectFit: 'contain',
                                        width: '100%',
                                        height: '100%',
                                        maxHeight: '150px',
                                      }}
                                    />
                                  ) : (
                                    <div
                                      className='d-flex align-items-center justify-content-center'
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                      }}
                                    >
                                      <i className='bi bi-image fs-4x'></i>
                                    </div>
                                  )}
                                  <button
                                    className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                                    type='button'
                                    style={{
                                      position: 'absolute',
                                      top: '5px',
                                      right: '5px',
                                    }}
                                    onClick={() => {
                                      const input = document.createElement('input')
                                      input.type = 'file'
                                      input.accept = '.png, .jpg, .jpeg'
                                      input.onchange = (e: any) => {
                                        if (e.target.files && e.target.files.length > 0) {
                                          setCrop(undefined)
                                          const reader = new FileReader()
                                          reader.addEventListener('load', () => {
                                            setTempImage(reader.result?.toString() || '')
                                            setResizeImageModal(true)
                                            setSelectedImageIndex(index)
                                          })
                                          reader.readAsDataURL(e.target.files[0])
                                        }
                                      }
                                      input.click()
                                    }}
                                  >
                                    <i className='ki-outline ki-pencil fs-7'></i>
                                  </button>
                                  {(prop.values.imagesData?.[index]?.imageFullUrl ||
                                    prop.values.imagesData?.[index]?.imageUrl) && (
                                    <button
                                      className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                                      type='button'
                                      style={{
                                        position: 'absolute',
                                        top: '5px',
                                        right: '35px',
                                      }}
                                      onClick={() => {
                                        const filteredImages = prop.values.imagesData?.filter(
                                          (image: any, i: number) => i !== index
                                        )
                                        prop.setFieldValue('imagesData', filteredImages)
                                      }}
                                    >
                                      <i className='ki-outline ki-cross fs-7'></i>
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>

                            {prop.errors.imagesData && prop.touched.imagesData && (
                              <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                <div className='fv-help-block'>{prop.errors.imagesData}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className='card card-flush py-4'>
                        <div className='card-header'>
                          <div className='card-title m-0'>
                            <div>
                              <h3 className='fw-bold mb-2 mt-5'>Additional Information</h3>
                              <p className='text-muted fw-normal fs-6'>
                                Highlight the key advantages and value of the product to inform
                                potential customers.
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className='card-body w-100 d-flex flex-column gap-5'>
                          <div className='row'>
                            <div
                              className='accordion accordion-icon-collapse w-100 d-flex flex-column'
                              id='kt_accordion_3'
                            >
                              {prop.values.infoContentData?.map((item, index) => {
                                return (
                                  <div className='row' key={index}>
                                    <div className='mb-5'>
                                      <div
                                        className='accordion-header collapsed py-3 d-flex'
                                        data-bs-toggle='collapse'
                                        data-bs-target={`#kt_accordion_3_item_${index + 1}`}
                                      >
                                        <span className='accordion-icon'>
                                          <i className='ki-duotone ki-plus-square fs-3 accordion-icon-off'>
                                            <span className='path1'></span>
                                            <span className='path2'></span>
                                            <span className='path3'></span>
                                          </i>
                                          <i className='ki-duotone ki-minus-square fs-3 accordion-icon-on'>
                                            <span className='path1'></span>
                                            <span className='path2'></span>
                                          </i>
                                        </span>
                                        <h3
                                          className={`fs-4 fw-bold mb-0 ms-4  ${
                                            // @ts-ignore
                                            prop.errors.infoContentData?.[index]?.data
                                              ? 'text-danger'
                                              : ''
                                          }`}
                                        >
                                          {item.name}
                                        </h3>
                                        <span className='position-relative'>
                                          <OverlayTrigger
                                            placement='top'
                                            overlay={
                                              <Tooltip>
                                                {
                                                  tooltips.find((t) => t.title === item.name)
                                                    ?.content
                                                }
                                              </Tooltip>
                                            }
                                          >
                                            <i className='bi bi-info-circle text-primary ms-1'></i>
                                          </OverlayTrigger>
                                        </span>
                                      </div>
                                      <div
                                        id={`kt_accordion_3_item_${index + 1}`}
                                        className='collapse fs-6 ps-10'
                                        data-bs-parent='#kt_accordion_3'
                                      >
                                        <div className='row py-5'>
                                          <div className='col-10'>
                                            {index === 0 && <QuillToolbar2 />}
                                            {index === 1 && <QuillToolbar3 />}
                                            {index === 2 && <QuillToolbar4 />}
                                            {index === 3 && <QuillToolbar5 />}
                                            <ReactQuill
                                              placeholder={intl.formatMessage({
                                                id: 'MEMBER.DETAIL.PACKAGE.DESCRIPTION',
                                              })}
                                              modules={
                                                index === 0
                                                  ? modules_2
                                                  : index === 1
                                                  ? modules_3
                                                  : index === 2
                                                  ? modules_4
                                                  : modules_5
                                              }
                                              formats={formats}
                                              value={item.data}
                                              onChange={(value) => {
                                                prop.setFieldValue(
                                                  `infoContentData[${index}].data`,
                                                  value
                                                )
                                              }}
                                            />
                                            <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                              {prop.errors.infoContentData?.[index] &&
                                                // @ts-ignore
                                                prop.errors.infoContentData?.[index]?.data && (
                                                  <div className='fv-help-block'>
                                                    {/* @ts-ignore */}
                                                    {prop.errors.infoContentData?.[index]?.data}
                                                  </div>
                                                )}
                                            </div>
                                          </div>
                                        </div>

                                        {/*  <p className='mt-10'>{item.content}</p> */}
                                      </div>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='alert alert-primary d-flex align-items-center p-5 mt-10'>
                      <i className='ki-duotone ki-shield-tick fs-2hx text-primary me-4'>
                        <span className='path1'></span>
                        <span className='path2'></span>
                      </i>

                      <div className='d-flex flex-column'>
                        <h4 className='mb-1 text-primary'>Please Note</h4>

                        <span>
                          To maintain accuracy in transactions and reports, products{' '}
                          <b>cannot be edited once a sale has been made.</b> If you need to make
                          changes, you can <b>deactivate the existing product</b> and create a new
                          one with the updated details.
                        </span>
                      </div>
                    </div>
                    <div className='d-flex justify-content-end mt-10'>
                      <button
                        type='submit'
                        className='btn btn-dark px-15'
                        disabled={isSaving || isEditing}
                      >
                        {isSaving || isEditing ? (
                          <span
                            className='spinner-border spinner-border-sm'
                            role='status'
                            aria-hidden='true'
                          ></span>
                        ) : (
                          intl.formatMessage({id: 'GLOBAL.SAVE'})
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          </>
        )
      }}
    </Formik>
  )
}
